'use client'

import type React from 'react'
import Image from 'next/image'
import { CheckIcon } from '@heroicons/react/24/solid'
import useTranslation from 'next-translate/useTranslation'

import { ReactElement } from 'react'
import Button from '@/components/atoms/Button'
import Avatar from '@/components/atoms/Avatar'
import CustomHead from '@/components/molecules/CustomHead'
import { WithEmployerHeaderLayout } from '@/components/layouts'

export default function ThinkItMagazynHr() {
  const { t } = useTranslation('forEmployers')

  const handleDownload = () => {
    window.location.href = '/resources/think-it-8/download'
  }

  return (
    <>
      <CustomHead
        metaData={{
          title: t('thinkItTitle'),
          description: t('thinkItDescription'),
        }}
      />
      <main className='min-h-screen'>
        {/* Hero Section */}
        <section className='bg-primary px-6 py-16 md:py-24'>
          <div className='container-medium mx-auto'>
            <div className='grid gap-8 md:grid-cols-2 md:items-center'>
              <div className='space-y-6'>
                <h1 className='text-7xl font-bold leading-tight text-white md:text-6xl lg:text-8xl'>
                  Już jest!
                  <br />
                  Nowy numer
                </h1>
                <div className='w-full bg-[#6C3FED] px-8 py-4 text-4xl font-bold text-white md:text-5xl lg:text-6xl'>
                  Think IT Magazyn HR
                </div>
                <div>
                  <Button
                    type='white'
                    size='big'
                    className='border-white'
                    onClick={handleDownload}
                  >
                    Pobierz magazyn »
                  </Button>
                </div>
              </div>
              <div className='flex justify-center'>
                <div className='relative w-full'>
                  <Image
                    src='/next-images/think-it/think-it-8-cover.png'
                    alt='Think IT Magazyn HR Cover'
                    width={530}
                    height={703}
                    className='w-full h-auto transform rotate-3 hover:rotate-0 transition-transform duration-300'
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className='bg-white px-6 py-16 md:py-24'>
          <div className='container-medium mx-auto'>
            <div className='grid gap-12 md:grid-cols-2 md:items-center'>
              <div className='flex justify-center'>
                <Image
                  src='/next-images/think-it/think-it-8-inner.png'
                  alt='Magazine Preview'
                  width={900}
                  height={759}
                  className='w-full h-auto'
                />
              </div>
              <div className='space-y-6'>
                <h2 className='text-3xl font-bold text-gray-900 md:text-4xl'>
                  W Think IT Magazyn HR nr 8 znajdziesz:
                </h2>
                <div className='space-y-4'>
                  <div className='flex gap-3'>
                    <div className='flex-shrink-0'>
                      <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[#6C3FED]'>
                        <CheckIcon className='h-4 w-4 text-white' />
                      </div>
                    </div>
                    <div>
                      <p className='text-lg'>
                        <span className='font-bold'>
                          Teksty od specjalistów
                        </span>{' '}
                        HR, EB i radcy prawnego oraz zestawienie danych.
                      </p>
                    </div>
                  </div>
                  <div className='flex gap-3'>
                    <div className='flex-shrink-0'>
                      <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[#6C3FED]'>
                        <CheckIcon className='h-4 w-4 text-white' />
                      </div>
                    </div>
                    <div>
                      <p className='text-lg'>
                        <span className='font-bold'>Case study</span> dotyczące
                        D&I.
                      </p>
                    </div>
                  </div>
                  <div className='flex gap-3'>
                    <div className='flex-shrink-0'>
                      <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[#6C3FED]'>
                        <CheckIcon className='h-4 w-4 text-white' />
                      </div>
                    </div>
                    <div>
                      <p className='text-lg'>
                        <span className='font-bold'>Wskazówki,</span> dobre
                        praktyki i różne perspektywy.
                      </p>
                    </div>
                  </div>
                  <div className='flex gap-3'>
                    <div className='flex-shrink-0'>
                      <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[#6C3FED]'>
                        <CheckIcon className='h-4 w-4 text-white' />
                      </div>
                    </div>
                    <div>
                      <p className='text-lg'>
                        <span className='font-bold'>Recenzje</span> książek.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What You'll Read Section */}
        <section className='bg-white px-6 py-16 md:py-24'>
          <div className='container-medium mx-auto'>
            <h2 className='text-3xl font-bold text-gray-900 mb-12 md:text-4xl'>
              O czym przeczytasz?
            </h2>
            <div className='grid gap-12 md:grid-cols-2 md:items-start'>
              <div className='space-y-6'>
                <div className='flex gap-3'>
                  <div className='flex-shrink-0'>
                    <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[#6C3FED]'>
                      <CheckIcon className='h-4 w-4 text-white' />
                    </div>
                  </div>
                  <div>
                    <p className='text-lg'>
                      <span className='font-bold'>
                        Jak HR może mądrze korzystać ze sztucznej inteligencji,
                      </span>{' '}
                      stosując 6 narzędzi, które realnie usprawniają rekrutację,
                      komunikację i codzienną pracę w HR i EB.
                    </p>
                  </div>
                </div>
                <div className='flex gap-3'>
                  <div className='flex-shrink-0'>
                    <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[#6C3FED]'>
                      <CheckIcon className='h-4 w-4 text-white' />
                    </div>
                  </div>
                  <div>
                    <p className='text-lg'>
                      <span className='font-bold'>
                        Jak wdrożyć wartości D&I w procesach firmy
                      </span>
                      , aby budować inkluzywną kulturę, nawet jeżeli dopiero
                      zaczynasz w obszarze różnorodności.
                    </p>
                  </div>
                </div>
                <div className='flex gap-3'>
                  <div className='flex-shrink-0'>
                    <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[#6C3FED]'>
                      <CheckIcon className='h-4 w-4 text-white' />
                    </div>
                  </div>
                  <div>
                    <p className='text-lg'>
                      <span className='font-bold'>
                        Czy efektywny dialog w firmie jest możliwy?
                      </span>{' '}
                      Z perspektywy radcy prawnego.
                    </p>
                  </div>
                </div>
                <div className='flex gap-3'>
                  <div className='flex-shrink-0'>
                    <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[#6C3FED]'>
                      <CheckIcon className='h-4 w-4 text-white' />
                    </div>
                  </div>
                  <div>
                    <p className='text-lg'>
                      <span className='font-bold'>
                        Dowiesz się, jak przeżyć podróż Koleją Transsyberyjską,
                      </span>
                      nie wychodząc z domu.
                    </p>
                  </div>
                </div>
              </div>
              <div className='flex justify-center md:justify-end'>
                <Image
                  src='/next-images/think-it/think-it-8-dog.png'
                  alt='Magazine Content'
                  width={450}
                  height={500}
                  className='w-full max-w-md h-autotransform -rotate-2'
                />
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className='bg-teal px-6 py-16 md:py-24'>
          <div className='container-medium mx-auto max-w-7xl'>
            <h2 className='text-3xl font-bold text-gray-900 mb-12 text-center md:text-4xl'>
              Opinie o Think IT Magazyn HR
            </h2>
            <div className='grid gap-8 md:grid-cols-2 lg:grid-cols-4'>
              <div className='flex flex-col'>
                <p className='text-md leading-relaxed text-gray-800 italic'>
                  Magazyn czyta się bardzo płynnie, jest napisany prostym
                  językiem. Brawo za rewelacyjny pomysł z konkursem dla GFT!
                  KUDOS!
                </p>
                <div className='flex flex-col items-center gap-3 pt-4 mt-auto'>
                  <Avatar
                    alt='Artur Augustyniak'
                    url='/next-images/think-it/artur-augystyniak.png'
                    size='big'
                  />
                  <div className='text-center'>
                    <p className='font-bold text-gray-900'>Artur Augustyniak</p>
                    <p className='text-sm text-gray-600'>
                      Team Manager / Lead Software Developer
                    </p>
                  </div>
                </div>
              </div>

              <div className='flex flex-col'>
                <p className='text-md leading-relaxed text-gray-800 italic'>
                  Świetny pomysł na magazyn. Dobra forma, krótko, zwięźle i
                  estetycznie. Case EB i okładka – złoto. Czekam na kolejny
                  numer.
                </p>
                <div className='flex flex-col items-center gap-3 pt-4 mt-auto'>
                  <Avatar
                    alt='Kasia Machowska'
                    url='/next-images/think-it/kasia-machowska.png'
                    size='big'
                  />
                  <div className='text-center'>
                    <p className='font-bold text-gray-900'>Kasia Machowska</p>
                    <p className='text-sm text-gray-600'>Asystentka Zarządu</p>
                  </div>
                </div>
              </div>

              <div className='flex flex-col'>
                <p className='text-md leading-relaxed text-gray-800 italic'>
                  Wygląda ciekawie i obiecująco - brakowało na rynku czegoś w
                  tym stylu. Plus ogromne brawa za oprawę graficzną, naprawdę
                  przyciąga uwagę.
                </p>
                <div className='flex flex-col items-center gap-3 pt-4 mt-auto'>
                  <Avatar
                    alt='Marta Michałowska'
                    url='/next-images/think-it/marta-michalowska.png'
                    size='big'
                  />
                  <div className='text-center'>
                    <p className='font-bold text-gray-900'>Marta Michałowska</p>
                    <p className='text-sm text-gray-600'>Head of Marketing</p>
                  </div>
                </div>
              </div>

              <div className='flex flex-col'>
                <p className='text-md leading-relaxed text-gray-800 italic'>
                  Mnóstwo merytoryki w obszarze HR i IT w jednym miejscu. Jestem
                  zachwycona case study, Rafała Garbarczyka, który dzieli się
                  swoim know – how. Treści bazujące na konkretnych przykładach z
                  życia wziętych! Strzał w 10.T!
                </p>
                <div className='flex flex-col items-center gap-3 pt-4 mt-auto'>
                  <Avatar
                    alt='Karolina Kałużna'
                    url='/next-images/think-it/karolina-kaluzna.png'
                    size='big'
                  />
                  <div className='text-center'>
                    <p className='font-bold text-gray-900'>Karolina Kałużna</p>
                    <p className='text-sm text-gray-600'>
                      Specjalista ds. HR i Rekrutacji
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section className='bg-[#6C3FED] px-6 py-16 md:py-24'>
          <div className='container mx-auto max-w-3xl'>
            <div className='text-center space-y-8'>
              <p className='text-4xl font-bold text-white'>
                Dołącz do czytelników
                <br />
                Think IT Magazyn HR.
              </p>
              <p className='text-lg text-white'>
                Odbierz swój dostęp do najnowszego numeru całkowicie za darmo.
              </p>
              <Button type='primary' size='big' onClick={handleDownload}>
                Pobierz magazyn »
              </Button>
            </div>
          </div>
        </section>
      </main>
    </>
  )
}

ThinkItMagazynHr.getLayout = function getLayout(page: ReactElement) {
  return <WithEmployerHeaderLayout>{page}</WithEmployerHeaderLayout>
}
